import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ClockCounterClockwise, CircleNotch } from "@phosphor-icons/react";
import { NetMovementChip } from "@/components/NetMovementChip";

const timeAgo = (iso) => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const TYPE_COLORS = {
  transaction_created: "var(--bk-success)",
  transaction_updated: "var(--bk-accent)",
  transaction_deleted: "#B23B1F",
  transactions_imported: "#2B5EA7",
  plaid_import_finalized: "#2B5EA7",
  plaid_import_auto: "#2B5EA7",
  invoice_created: "var(--bk-gold)",
  subscription_cancel_reminder: "#D96C4A",
  team_member_invited: "#6B4BA3",
  team_member_accepted: "#6B4BA3",
  team_member_revoked: "#B23B1F",
};

export function TeamActivityTimeline({ businessId }) {
  const [data, setData] = useState(null);
  const [memberId, setMemberId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (mid) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/businesses/${businessId}/activity${mid ? `?member_id=${mid}` : ""}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(memberId); }, [load, memberId]);

  const members = data?.members || [];
  const events = data?.events || [];

  return (
    <section className="bk-card p-6" data-testid="team-activity-timeline">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-display text-xl font-bold flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
            <ClockCounterClockwise size={20} weight="duotone" color="var(--bk-accent)" /> Activity timeline
          </h4>
          <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Who did what across this business — filter by member.</p>
        </div>
        <NetMovementChip businessId={businessId} variant="compact" testId="activity-net-movement-chip" />
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4" data-testid="activity-member-filters">
        <button
          type="button"
          onClick={() => setMemberId("")}
          data-testid="activity-filter-all"
          className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
          style={memberId === "" ? { background: "var(--bk-accent)", borderColor: "var(--bk-accent)", color: "#fff" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
        >
          All activity
        </button>
        {members.map((m) => (
          <button
            key={m.member_id}
            type="button"
            onClick={() => setMemberId(m.member_id)}
            data-testid={`activity-filter-${m.member_id}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
            style={memberId === m.member_id ? { background: "var(--bk-accent)", borderColor: "var(--bk-accent)", color: "#fff" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
          >
            {m.name} · {m.role}{m.event_count ? ` (${m.event_count})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-5 text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }} data-testid="activity-loading">
          <CircleNotch size={16} className="animate-spin" /> Loading activity…
        </div>
      ) : events.length === 0 ? (
        <div className="mt-5 rounded-2xl border px-4 py-4 text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }} data-testid="activity-empty">
          No activity recorded yet{memberId ? " for this member" : ""}. New transactions, imports, and invoices will show up here.
        </div>
      ) : (
        <div className="mt-5 space-y-0 relative" data-testid="activity-event-list">
          <div className="absolute left-[5px] top-2 bottom-2 w-px" style={{ background: "var(--bk-border)" }} />
          {events.map((e) => (
            <div key={e.id} className="relative pl-6 pb-4 last:pb-0" data-testid={`activity-event-${e.id}`}>
              <span className="absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-white" style={{ background: TYPE_COLORS[e.type] || "var(--bk-text-2)" }} />
              <p className="text-sm" style={{ color: "var(--bk-text)" }}>{e.description}</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>
                {e.actor_name} · {e.actor_role} · {timeAgo(e.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
